import {
  AnnouncementOutlined,
  ArrowForward,
  Call,
  CallMade,
  CallOutlined,
  CampaignOutlined,
  ErrorOutline,
  Info,
  KeyboardArrowDown,
} from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  InputBase,
  Menu,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ErrorModal from "../modals/ErrorModal";
import SuccessModal from "../modals/SuccessModal";
import { jwtDecode } from "jwt-decode";
import {
  formatPhoneNumber,
  getPhoneOptionLabel,
  toDialableNumber,
} from "../utils/formatnumber";
import posthog from "posthog-js";
import decryptApi from "../utils/decryptApi";
import Front from "@frontapp/plugin-sdk";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Device } from "@twilio/voice-sdk";
import OngoingCallModal from "../modals/OngoingCallModal";
import TwilioTrialBanner from "../reusables/TwilioTrialBanner";
import { ChevronDown, ChevronUp, PhoneOutgoing, TriangleAlert } from "lucide-react";
import { brand } from "../../theme/brandTokens";
import {
  getOnTwilioTrial,
  ensureTrialStatus,
} from "../../utils/twilioTrial";
import { useAssistantUiBridge } from "../../assistant";
import {
  captureAnalyticsEvent,
  capturePostHogLog,
  getJwtTtlSeconds,
} from "../../utils/posthogAnalytics";
import {
  getDialResultMessage,
  getMicPreflightErrorMessage,
  getTwilioErrorMessage,
  getUnexpectedDisconnectMessage,
  preflightMicrophone,
  shouldSurfaceUnexpectedDisconnect,
} from "../../utils/twilioErrorMessages";
import {
  fetchDialResultWithRetry,
  getUnansweredOutcome,
  shouldShowDialResultError,
} from "../../utils/twilioLookupDialResult";
import {
  completeOutboundCallNotification,
  readOutboundNotificationConfig,
  shouldSyncOutboundNotification,
} from "../../utils/outboundNotificationSettings";
import { getAppEnvironment } from "../../utils/appEnvironment";
import { getDefaultForwardingNumberForUser } from "../../utils/forwardNumberChannels";
import { suppressInterruptedPlayRejection } from "../../utils/suppressInterruptedPlay";
import RecipientContactInput, {
  resolveContactByPhone,
} from "./RecipientContactInput";
import DialerAudioOptions from "./DialerAudioOptions";
import {
  findRecording,
  readDialerPreferences,
  saveDialerPreferences,
} from "../../utils/dialerPreferences";
import { createGreetingMixer } from "../../utils/greetingMixer";

const Dailer = () => {
  const assistantBridge = useAssistantUiBridge();
  const callMetaRef = useRef({ startedAt: null, recordingEnabled: false, transcriptionEnabled: false });
  const outboundCompleteRef = useRef(null);
  const userHangupRef = useRef(false);
  const voicemailDropRef = useRef(false);
  const browserCallAttemptRef = useRef(0);
  const [organization, setOrganization] = useState(
    JSON.parse(localStorage.getItem("organization"))
  );
  const [searchParams, setSearchParams] = useSearchParams();

  // Get recording and transcription settings for outbound calls
  const recordingSettings = organization?.recording_transcription_config?.M || {};
  const outboundRecordingPolicy = recordingSettings.outbound_recording?.S || "disallow";
  const outboundTranscriptionPolicy = recordingSettings.outbound_transcription?.S || "disallow";

  // Get user ID for localStorage keys
  const userId = JSON.parse(localStorage.getItem("user"))?.userId?.S || "default";
  const recordingPreferenceKey = `recording_preference_${userId}`;
  const transcriptionPreferenceKey = `transcription_preference_${userId}`;
  const outboundNotificationConfig = readOutboundNotificationConfig(organization);
  const outboundNotificationChannelId = outboundNotificationConfig.channel_id;
  const outboundNotificationPolicy = outboundNotificationConfig.policy || "allow";
  const hasNotificationChannel = !!outboundNotificationChannelId;
  const notificationPreferenceKey = `outbound_notification_preference_${userId}`;

  // State for user-controlled toggles (only used when policy is "allow")
  const [enableRecording, setEnableRecording] = useState(() => {
    const policy = recordingSettings.outbound_recording?.S || "disallow";
    if (policy === "require") {
      return true;
    } else if (policy === "disallow") {
      return false;
    } else {
      // Policy is "allow" - check localStorage for user preference
      const savedPreference = localStorage.getItem(recordingPreferenceKey);
      return savedPreference !== null ? savedPreference === "true" : true; // Default to true if no preference
    }
  });
  const [enableTranscription, setEnableTranscription] = useState(() => {
    const recPolicy = recordingSettings.outbound_recording?.S || "disallow";
    const transPolicy = recordingSettings.outbound_transcription?.S || "disallow";
    
    if (transPolicy === "require") {
      return true;
    } else if (transPolicy === "disallow" || recPolicy === "disallow") {
      return false;
    } else {
      // Policy is "allow" - check localStorage for user preference
      // First check if recording is enabled (either required or from localStorage)
      const recPolicyIsAllow = recPolicy === "allow";
      const recordingEnabled = recPolicy === "require" || 
        (recPolicyIsAllow && localStorage.getItem(recordingPreferenceKey) === "true");
      
      if (!recordingEnabled) {
        return false;
      }
      
      const savedPreference = localStorage.getItem(transcriptionPreferenceKey);
      return savedPreference !== null ? savedPreference === "true" : false; // Default to false if no preference
    }
  });
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const prefsOrgId = organization?.orgId?.S;
  const [dialerPrefs, setDialerPrefs] = useState(() =>
    readDialerPreferences(JSON.parse(localStorage.getItem("user") || "null"), prefsOrgId)
  );
  const [prefsSaveError, setPrefsSaveError] = useState(null);
  const [recordings, setRecordings] = useState(
    () => JSON.parse(localStorage.getItem("organization"))?.recordings?.L || []
  );
  const greetingRecording = findRecording(recordings, dialerPrefs.greeting_rec_id);
  const vmDropRecording = findRecording(recordings, dialerPrefs.vm_drop_rec_id);

  const handleDialerPrefsChange = async (patch) => {
    const previous = dialerPrefs;
    setDialerPrefs((prev) => ({ ...prev, ...patch }));
    setPrefsSaveError(null);
    try {
      const saved = await saveDialerPreferences(prefsOrgId, patch);
      setDialerPrefs(saved);
    } catch (error) {
      console.error(
        "Dailer: save dialer preferences failed",
        error.response?.data || error.message || error
      );
      setDialerPrefs(previous);
      setPrefsSaveError("Couldn’t save that change. Please try again.");
    }
  };
  const [enableNotification, setEnableNotification] = useState(() => {
    if (!hasNotificationChannel) {
      return false;
    }
    if (outboundNotificationPolicy === "require") {
      return true;
    }
    if (outboundNotificationPolicy === "allow") {
      const savedPreference = localStorage.getItem(notificationPreferenceKey);
      return savedPreference !== null ? savedPreference === "true" : true;
    }
    return false;
  });

  // Update transcription when recording changes (if transcription is allowed)
  useEffect(() => {
    if (!enableRecording && outboundTranscriptionPolicy === "allow") {
      setEnableTranscription(false);
      // Clear transcription preference when recording is disabled
      localStorage.removeItem(transcriptionPreferenceKey);
    }
  }, [enableRecording, outboundTranscriptionPolicy, transcriptionPreferenceKey]);

  // Save recording preference to localStorage when it changes (only if policy is "allow")
  useEffect(() => {
    if (outboundRecordingPolicy === "allow") {
      localStorage.setItem(recordingPreferenceKey, enableRecording.toString());
    }
  }, [enableRecording, outboundRecordingPolicy, recordingPreferenceKey]);

  // Save transcription preference to localStorage when it changes (only if policy is "allow")
  useEffect(() => {
    if (outboundTranscriptionPolicy === "allow" && outboundRecordingPolicy !== "disallow") {
      localStorage.setItem(transcriptionPreferenceKey, enableTranscription.toString());
    }
  }, [enableTranscription, outboundTranscriptionPolicy, outboundRecordingPolicy, transcriptionPreferenceKey]);

  useEffect(() => {
    if (outboundNotificationPolicy === "allow" && hasNotificationChannel) {
      localStorage.setItem(notificationPreferenceKey, enableNotification.toString());
    }
  }, [
    enableNotification,
    outboundNotificationPolicy,
    hasNotificationChannel,
    notificationPreferenceKey,
  ]);

  // Update states when organization changes
  useEffect(() => {
    const recPolicy = recordingSettings.outbound_recording?.S || "disallow";
    const transPolicy = recordingSettings.outbound_transcription?.S || "disallow";
    
    if (recPolicy === "require") {
      setEnableRecording(true);
    } else if (recPolicy === "disallow") {
      setEnableRecording(false);
    } else if (recPolicy === "allow") {
      // Load user preference from localStorage
      const savedPreference = localStorage.getItem(recordingPreferenceKey);
      if (savedPreference !== null) {
        setEnableRecording(savedPreference === "true");
      }
    }
    
    if (transPolicy === "require") {
      setEnableTranscription(true);
    } else if (transPolicy === "disallow" || recPolicy === "disallow") {
      setEnableTranscription(false);
    } else if (transPolicy === "allow" && recPolicy !== "disallow") {
      // Load user preference from localStorage, but only if recording is enabled
      if (recPolicy === "require" || enableRecording) {
        const savedPreference = localStorage.getItem(transcriptionPreferenceKey);
        if (savedPreference !== null) {
          setEnableTranscription(savedPreference === "true");
        } else {
          setEnableTranscription(false);
        }
      } else {
        setEnableTranscription(false);
      }
    }

    if (!hasNotificationChannel) {
      setEnableNotification(false);
    } else if (outboundNotificationPolicy === "require") {
      setEnableNotification(true);
    } else if (outboundNotificationPolicy === "allow") {
      const savedPreference = localStorage.getItem(notificationPreferenceKey);
      if (savedPreference !== null) {
        setEnableNotification(savedPreference === "true");
      } else {
        setEnableNotification(true);
      }
    } else {
      setEnableNotification(false);
    }
  }, [organization, recordingPreferenceKey, transcriptionPreferenceKey, enableRecording, notificationPreferenceKey, hasNotificationChannel, outboundNotificationPolicy]);

  async function decodeToken(token) {
    try {
      return await decryptApi(token);
    } catch (error) {
      // Log when Twilio token decrypt fails
      console.error("Invalid token:", error);
      return null;
    }
  }

  const [call_data, setCallData] = useState(() => {
    const org = JSON.parse(localStorage.getItem("organization"));
    const currentUserId = JSON.parse(localStorage.getItem("user"))?.userId?.S;
    const hasBrowserCalling = !!org?.twilio_integration?.M?.app_sid?.S;
    return {
      fromNumber: org?.twilio_integration?.M?.primary_phone?.S,
      forwarding_number: getDefaultForwardingNumberForUser(org, currentUserId),
      recipient: "",
      callMethod: hasBrowserCalling ? "browser_call" : "forwarding_device",
    };
  });
  const [selectedContact, setSelectedContact] = useState(null);
  const [recipientError, setRecipientError] = useState(null);

  const prefillLookupIdRef = useRef(0);
  const resolvePrefillContact = useCallback(async (phone) => {
    const lookupId = ++prefillLookupIdRef.current;
    setSelectedContact(null);
    const orgId = JSON.parse(localStorage.getItem("organization"))?.orgId?.S;
    if (!orgId || !phone) return;
    const contact = await resolveContactByPhone(orgId, phone);
    if (lookupId === prefillLookupIdRef.current) {
      setSelectedContact(contact);
    }
  }, []);

  const [onTwilioTrial, setOnTwilioTrial] = useState(
    () => getOnTwilioTrial(organization) === true
  );

  useEffect(() => {
    let cancelled = false;
    const orgId = organization?.orgId?.S;
    (async () => {
      const status = await ensureTrialStatus(orgId);
      if (cancelled) return;
      const org = JSON.parse(localStorage.getItem("organization"));
      if (org) setOrganization(org);
      setOnTwilioTrial(
        status.checkFailed
          ? getOnTwilioTrial(org) === true
          : status.onTwilioTrial === true
      );
    })();
    return () => {
      cancelled = true;
    };
    // Refresh once on dialer mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let latestContext;

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const subscribe = Front.contextUpdates.subscribe((context) => {
      latestContext = context;
      let link;

      latestContext.listMessages().then((response) => {
        if (response.results.length > 0) {
          const latestMessageIndex = response.results.length - 1;
          let body = response.results[latestMessageIndex].content.body;

          const path = body.match(/twilanswer\.com(\/[^\s"]+)/)?.[1];

          link = path;
        }

        if (latestContext.entryPoint) {
          if (
            latestContext.entryPoint &&
            latestContext.entryPoint.type == "CONTACT_HANDLE"
          ) {
            const handle = latestContext.entryPoint.data.handle;
            setCallData((prev) => ({
              ...prev,
              recipient: handle,
            }));
            resolvePrefillContact(handle);
          }
        }
      });
    });
    return () => {
      subscribe.unsubscribe();
    };
  }, []);

  const [err_msg, setErrorMsg] = useState(null);
  const [loader, setLoader] = useState(false);
  const [succeesModal, setSuccessModal] = useState(false);
  const [errorModal, setErrrorModal] = useState(false);
  const [from_numbers, setFromNumbers] = useState([
    {
      phoneNumber: JSON.parse(localStorage.getItem("organization")).twilio_integration.M.primary_phone.S,
      friendlyName: "Primary Phone Number",
    }
  ]);
  const [phoneNumbersLoaded, setPhoneNumbersLoaded] = useState(false);
  const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState(false);

  async function getIncomingPhoneNumbers() {
    // Don't fetch if already loaded
    if (phoneNumbersLoaded) {
      return;
    }
    
    setLoadingPhoneNumbers(true);
    setErrorMsg(null);
    const org = JSON.parse(localStorage.getItem("organization"));
    await axios
      .post(
        process.env.REACT_APP_BACKEND_URL + "twilio/get-incoming-phone-numbers",
        { organization_id: org.orgId.S }
      )
      .then((res) => {
        let data = res.data;
        if (res.data.length < 1) {
          setErrorMsg(
            "please add an incoming phone number in your twilio account"
          );
          setLoadingPhoneNumbers(false);
        } else {
          setFromNumbers(res.data);
          setPhoneNumbersLoaded(true);
          setLoadingPhoneNumbers(false);
        }
      })
      .catch((err) => {
        // Log for production diagnostics when fetching phone numbers fails
        console.error("Dailer: fetch phone numbers failed", err.response?.data || err.message || err);
        if (err.response && err.response.data) {
          setErrorMsg(err.response.data.message);
        }
        setLoadingPhoneNumbers(false);
      });
  }

  const call = async (toNumber) => {
    captureAnalyticsEvent("call_attempt_started", {
      call_method: "forwarding_device",
      device_state: null,
      device_ready: null,
    });

    const storedUser = JSON.parse(localStorage.getItem("user") || "null");
    const userId = storedUser?.userId?.S;
    if (!userId) {
      setErrorMsg(
        "We ran into an authentication issue and can’t verify your session. Please log out and log back in."
      );
      setErrrorModal(true);
      return;
    }

    let check = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}get-user-by-id`,
      {
        userId,
      }
    );

    if (check.status === 200) {
      const user___ = check.data.user;

      if (
        user___.relatedOrgs.L?.find(
          (e) =>
            e?.M.org_id?.S ===
            JSON.parse(localStorage.getItem("organization")).orgId.S
        )
      ) {
        try {
          setLoader(true);

          // Determine recording and transcription based on policy and user selection
          const shouldRecord = outboundRecordingPolicy === "require" ||
                              (outboundRecordingPolicy === "allow" && enableRecording);
          const shouldTranscribe = outboundTranscriptionPolicy === "require" ||
                                  (outboundTranscriptionPolicy === "allow" && enableTranscription && shouldRecord);
          const syncNotification = shouldSyncOutboundNotification({
            channelId: outboundNotificationChannelId,
            policy: outboundNotificationPolicy,
            userEnabled: enableNotification,
          });

          const orgId = JSON.parse(localStorage.getItem("organization")).orgId.S;
          const user = storedUser;
          const callUser = `${user?.fName?.S || ""} ${user?.lName?.S || ""}`.trim();
          const callUserId = user?.userId?.S;

          // Create call record first so we have a call_id for Twilio
          let call_ = await axios.post(
            process.env.REACT_APP_BACKEND_URL + "call/create-call",
            {
              organizationId: JSON.parse(localStorage.getItem("organization"))
                .orgId.S,
              startTime: Math.floor(Date.now() / 1000),
              toNumber,
              fromNumber: call_data.fromNumber,
              action: "Outbound Call",
              action_details: call_data.forwarding_number,
              has_vm: false,
              ...(callUser ? { call_user: callUser } : {}),
              ...(callUserId ? { call_user_id: callUserId } : {}),
              record: shouldRecord,
              transcribe: shouldTranscribe,
              ...(syncNotification
                ? { channel_id: outboundNotificationChannelId }
                : {}),
            }
          );

          const callId = call_.data?.call?.callId?.S;

          const twilioPayload = {
            fromNumber: call_data.fromNumber,
            forwarding_number: call_data.forwarding_number,
            recipient: toNumber,
            callMethod: call_data.callMethod,
            organization_id: orgId,
            record: shouldRecord,
            transcribe: shouldTranscribe,
          };
          if (callId) {
            twilioPayload.call_id = callId;
          }

          let twilio_call = await axios.post(
            process.env.REACT_APP_BACKEND_URL + "twilio/create-call",
            twilioPayload
          );
          let call_data_ = twilio_call.data.call;

          setSuccessModal(true);
          posthog.capture("Dailed a phone call", {
            email: user?.email?.S,
            userId: user?.userId?.S,
          });
          setLoader(false);
        } catch (error) {
          // Log for production diagnostics when placing call fails
          console.error("Dailer: place call failed", error.response?.data || error.message || error);
          setErrrorModal(true);
          const status = error?.response?.status;
          const apiError = error?.response?.data?.error;
          const apiMessage = error?.response?.data?.message;
          if (
            status === 401 &&
            typeof apiError === "string" &&
            apiError.toLowerCase().includes("authorization header")
          ) {
            setErrorMsg(
              "We ran into an authentication issue and can’t verify your session. Please log out and log back in."
            );
          } else if (error.response && error.response.data) {
            setErrorMsg([apiError, apiMessage].filter(Boolean).join(" "));
          }
          setLoader(false);
        }
      } else {
        window.location.reload();
      }
    }
  };
  const handleNavigation = (to) => {
    window.location.assign(to);
  };

  //new

  const [device, setdevice] = useState();
  const [deviceStatus, setDeviceStatus] = useState("idle"); // idle | connecting | registered | unregistered | error
  const [twilioCredsSnackbarOpen, setTwilioCredsSnackbarOpen] = useState(false);
  const [deviceMenuAnchor, setDeviceMenuAnchor] = useState(null);
  const deviceRef = useRef(null);
  const isSettingUpRef = useRef(false);
  const greetingMixerRef = useRef(null);

  const destroyDevice = () => {
    const current = deviceRef.current;
    if (current) {
      try {
        current.destroy();
        capturePostHogLog("info", "twilio_device_destroyed", {
          previous_state: current.state,
        });
      } catch (e) {
        // Ignore destroy errors
      }
    }
    deviceRef.current = null;
    setdevice(null);
    const mixer = greetingMixerRef.current;
    greetingMixerRef.current = null;
    mixer?.close();
  };

  const disconnectDevice = () => {
    setDeviceMenuAnchor(null);
    captureAnalyticsEvent("device_disconnect_clicked", {
      device_state: deviceRef.current?.state || deviceStatus,
    });
    destroyDevice();
    setDeviceStatus("idle");
  };

  const fetchTwilioToken = async ({ reason = "setup" } = {}) => {
    const org = JSON.parse(localStorage.getItem("organization"));
    if (!org?.orgId?.S) {
      capturePostHogLog("error", "twilio_token_fetched", {
        success: false,
        reason,
        error: "missing_organization_id",
      });
      return null;
    }

    try {
      const response = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "twilio/token",
        { organization_id: org.orgId.S }
      );

      const token = response.data.token || response.data;
      if (!token || typeof token !== "string") {
        console.error("Token must be a string, but got:", typeof token, token);
        capturePostHogLog("error", "twilio_token_fetched", {
          success: false,
          reason,
          error: "invalid_token_shape",
          token_type: typeof token,
        });
        return null;
      }

      const ttlSeconds = getJwtTtlSeconds(token);
      capturePostHogLog("debug", "twilio_token_fetched", {
        success: true,
        reason,
        ttl_seconds: ttlSeconds,
      });
      return token;
    } catch (error) {
      capturePostHogLog("error", "twilio_token_fetched", {
        success: false,
        reason,
        error: error?.response?.data?.error || error?.message || "token_request_failed",
        status: error?.response?.status,
      });
      throw error;
    }
  };

  const setUpDevice = async ({ force = false, source = "auto" } = {}) => {
    if (isSettingUpRef.current) {
      return false;
    }

    if (
      !force &&
      deviceRef.current &&
      deviceRef.current.state === "registered"
    ) {
      setDeviceStatus("registered");
      return true;
    }

    isSettingUpRef.current = true;
    setDeviceStatus("connecting");
    capturePostHogLog("info", "twilio_device_register_attempt", {
      force,
      source,
      previous_state: deviceRef.current?.state || deviceStatus,
    });

    try {
      const token = await fetchTwilioToken({ reason: force ? "reconnect" : "setup" });
      if (!token) {
        setDeviceStatus("error");
        setTwilioCredsSnackbarOpen(true);
        capturePostHogLog("error", "twilio_device_register_failed", {
          source,
          error: "token_unavailable",
        });
        return false;
      }

      const tokenTtlSeconds = getJwtTtlSeconds(token);
      destroyDevice();

      const newDevice = new Device(token, {
        debug: true,
      });

      newDevice.on("registered", () => {
        setDeviceStatus("registered");
        capturePostHogLog("info", "twilio_device_registered", {
          token_ttl_seconds: tokenTtlSeconds,
          source,
        });
      });

      newDevice.on("unregistered", () => {
        setDeviceStatus((prev) => (prev === "connecting" ? prev : "unregistered"));
        capturePostHogLog("warn", "twilio_device_unregistered", { source });
      });

      newDevice.on("error", (error) => {
        console.error("Twilio Device error:", error);
        capturePostHogLog("error", "twilio_device_error", {
          code: error?.code,
          message: error?.message,
          source,
        });
        if (error.code === 20101) {
          console.error(
            "Access token invalid - credentials may be incorrect or expired"
          );
          destroyDevice();
          setDeviceStatus("error");
          setTwilioCredsSnackbarOpen(true);
          setErrorMsg(getTwilioErrorMessage(error));
          setErrrorModal(true);
        } else {
          setDeviceStatus("error");
          setErrorMsg(getTwilioErrorMessage(error));
          setErrrorModal(true);
        }
      });

      newDevice.on("tokenWillExpire", async () => {
        capturePostHogLog("info", "twilio_token_will_expire", {
          remaining_ttl_seconds: getJwtTtlSeconds(token),
        });
        try {
          const refreshed = await fetchTwilioToken({ reason: "token_refresh" });
          if (refreshed) {
            newDevice.updateToken(refreshed);
            capturePostHogLog("info", "twilio_token_updated", {
              ttl_seconds: getJwtTtlSeconds(refreshed),
            });
          } else {
            setDeviceStatus("error");
            setTwilioCredsSnackbarOpen(true);
          }
        } catch (tokenError) {
          console.error("Error refreshing Twilio token:", tokenError);
          capturePostHogLog("error", "twilio_token_refresh_failed", {
            message: tokenError?.message,
          });
          setDeviceStatus("error");
        }
      });

      await newDevice.register();

      const mixer = createGreetingMixer();
      try {
        await newDevice.audio.addProcessor(mixer);
        greetingMixerRef.current = mixer;
      } catch (mixerError) {
        capturePostHogLog("warn", "greeting_mixer_unavailable", {
          message: mixerError?.message,
        });
      }

      deviceRef.current = newDevice;
      setdevice(newDevice);
      setDeviceStatus("registered");
      return true;
    } catch (error) {
      console.error("Error setting up device:", error);
      if (error?.response?.data) {
        console.error("Backend error:", error.response.data);
      }
      destroyDevice();
      setDeviceStatus("error");
      setTwilioCredsSnackbarOpen(true);
      capturePostHogLog("error", "twilio_device_register_failed", {
        source,
        message: error?.message,
        status: error?.response?.status,
        code: error?.code,
      });
      return false;
    } finally {
      isSettingUpRef.current = false;
    }
  };

  useEffect(() => {
    const onUnhandledRejection = (event) => {
      suppressInterruptedPlayRejection(event);
    };
    window.addEventListener("unhandledrejection", onUnhandledRejection, true);
    return () => {
      window.removeEventListener(
        "unhandledrejection",
        onUnhandledRejection,
        true
      );
    };
  }, []);

  useEffect(() => {
    if (call_data.callMethod !== "browser_call") {
      destroyDevice();
      setDeviceStatus("idle");
      return;
    }

    setUpDevice({ source: "page_load" });

    return () => {
      destroyDevice();
      setDeviceStatus("idle");
    };
    // Intentionally only re-run when call method changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call_data.callMethod]);

  const [openOngoingCallModal, setOpenOngoingCallModal] = useState(false);
  const [conn, setConn] = useState();
  const [activeCallInfo, setActiveCallInfo] = useState(null);
  const [callEndNotice, setCallEndNotice] = useState(null);

  const fireOutboundCompleteIfNeeded = () => {
    const payload = outboundCompleteRef.current;
    if (!payload) return;
    outboundCompleteRef.current = null;
    completeOutboundCallNotification(payload).catch((error) => {
      console.error(
        "Dailer: complete-call failed",
        error.response?.data || error.message || error
      );
    });
  };

  async function makeCall(toNumber) {
    const attemptId = ++browserCallAttemptRef.current;
    const deviceStateAtAttempt =
      deviceRef.current?.state || deviceStatus || "idle";

    captureAnalyticsEvent("call_attempt_started", {
      call_method: "browser_call",
      device_state: deviceStateAtAttempt,
      device_ready: deviceStateAtAttempt === "registered",
    });

    let activeDevice = deviceRef.current;
    if (!activeDevice || activeDevice.state !== "registered") {
      setLoader(true);
      const connected = await setUpDevice({
        force: true,
        source: "call_attempt",
      });
      activeDevice = deviceRef.current;
      if (!connected || !activeDevice || activeDevice.state !== "registered") {
        console.error("Twilio device not ready");
        capturePostHogLog("error", "twilio_call_blocked_device_not_ready", {
          device_state: deviceRef.current?.state || deviceStatus,
          device_state_at_attempt: deviceStateAtAttempt,
        });
        setLoader(false);
        setErrorMsg(
          "Browser calling isn’t ready. Click the Twilio status to reconnect, then try again."
        );
        setErrrorModal(true);
        return;
      }
    }

    setLoader(true);
    try {
      // Determine recording and transcription based on policy and user selection
      const shouldRecord = outboundRecordingPolicy === "require" || 
                          (outboundRecordingPolicy === "allow" && enableRecording);
      const shouldTranscribe = outboundTranscriptionPolicy === "require" || 
                              (outboundTranscriptionPolicy === "allow" && enableTranscription && shouldRecord);
      const syncNotification = shouldSyncOutboundNotification({
        channelId: outboundNotificationChannelId,
        policy: outboundNotificationPolicy,
        userEnabled: enableNotification,
      });

      const orgId = JSON.parse(localStorage.getItem("organization")).orgId.S;
      const user = JSON.parse(localStorage.getItem("user") || "null");
      const callUser = `${user?.fName?.S || ""} ${user?.lName?.S || ""}`.trim();
      const callUserId = user?.userId?.S;

      let call_ = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "call/create-call",
        {
          organizationId: orgId,
          startTime: Math.floor(Date.now() / 1000),
          toNumber,
          fromNumber: call_data.fromNumber,
          action: "Outbound Call",
          action_details: "Browser Call",
          has_vm: false,
          ...(callUser ? { call_user: callUser } : {}),
          ...(callUserId ? { call_user_id: callUserId } : {}),
          record: shouldRecord,
          transcribe: shouldTranscribe,
          ...(syncNotification
            ? { channel_id: outboundNotificationChannelId }
            : {}),
        }
      );

      const callId = call_.data?.call?.callId?.S;

      if (syncNotification && callId && outboundNotificationChannelId) {
        outboundCompleteRef.current = {
          organizationId: orgId,
          callId,
          channel_id: outboundNotificationChannelId,
        };
      } else {
        outboundCompleteRef.current = null;
      }

      const micPreflight = await preflightMicrophone();
      if (!micPreflight.ok) {
        capturePostHogLog("error", "twilio_mic_preflight_failed", {
          name: micPreflight.error?.name,
          message: micPreflight.error?.message,
          call_id: callId ?? null,
        });
        captureAnalyticsEvent("call_ended", {
          call_method: "browser_call",
          duration_seconds: 0,
          end_reason: "mic_preflight_failed",
          was_connected: false,
          call_id: callId ?? null,
        });
        fireOutboundCompleteIfNeeded();
        setErrorMsg(getMicPreflightErrorMessage(micPreflight.error));
        setErrrorModal(true);
        return;
      }

      capturePostHogLog("debug", "twilio_mic_preflight_ok", {
        call_id: callId ?? null,
      });

      const connection = await activeDevice.connect({
        params: {
          To: toNumber,
          Caller: call_data.fromNumber,
          Record: shouldRecord ? "true" : "false",
          Transcribe: shouldTranscribe ? "true" : "false",
          call_id: callId ?? "",
          orgId: orgId,
          environment: getAppEnvironment(),
          AnswerOnBridge: "true",
        },
      });

      setConn(connection);
      setActiveCallInfo({ orgId, callId: callId ?? null });
      setCallEndNotice(null);

      userHangupRef.current = false;
      voicemailDropRef.current = false;
      setOpenOngoingCallModal(true);

      // Twilio Voice SDK v2: disconnect/cancel/reject/error fire on the Call
      // (connection), not Device. Device listeners never ran in production.
      // Dial failures such as 13225 (call blocked) hang up the parent call
      // without an SDK `error` event — treat those as unexpected disconnects.
      callMetaRef.current = {
        startedAt: Date.now(),
        connectedAt: null,
        recordingEnabled: shouldRecord,
        transcriptionEnabled: shouldTranscribe,
        callId: callId ?? null,
        connectedCaptured: false,
      };

      let callEndNotified = false;
      let failureSurfaced = false;
      const notifyCallEnded = (endReason = "disconnect") => {
        if (callEndNotified) return;
        callEndNotified = true;
        const startedAt = callMetaRef.current.startedAt;
        const connectedAt = callMetaRef.current.connectedAt;
        const durationSeconds = connectedAt
          ? Math.max(0, Math.round((Date.now() - connectedAt) / 1000))
          : startedAt
          ? Math.max(0, Math.round((Date.now() - startedAt) / 1000))
          : null;

        captureAnalyticsEvent("call_ended", {
          call_method: "browser_call",
          duration_seconds: durationSeconds,
          end_reason: endReason,
          was_connected: Boolean(callMetaRef.current.connectedCaptured),
          call_id: callMetaRef.current.callId,
          recording_enabled: callMetaRef.current.recordingEnabled,
          transcription_enabled: callMetaRef.current.transcriptionEnabled,
        });

        assistantBridge?.notifyCallEnded?.({
          durationSeconds,
          recordingEnabled: callMetaRef.current.recordingEnabled,
          transcriptionEnabled: callMetaRef.current.transcriptionEnabled,
        });
        fireOutboundCompleteIfNeeded();
        callMetaRef.current.startedAt = null;
        callMetaRef.current.connectedAt = null;
      };

      const handleCallEnded = (endReason, { closeModal = true } = {}) => {
        if (closeModal) setOpenOngoingCallModal(false);
        notifyCallEnded(endReason);
      };

      const getCallSid = (call) =>
        call?.parameters?.CallSid || connection.parameters?.CallSid || null;

      const surfaceCallFailure = (message) => {
        if (failureSurfaced) return;
        failureSurfaced = true;
        setErrorMsg(message);
        setErrrorModal(true);
      };

      connection.on("accept", () => {
        callMetaRef.current.connectedAt = Date.now();
        callMetaRef.current.connectedCaptured = true;
        captureAnalyticsEvent("call_connected", {
          call_method: "browser_call",
          call_id: callMetaRef.current.callId,
          recording_enabled: callMetaRef.current.recordingEnabled,
          transcription_enabled: callMetaRef.current.transcriptionEnabled,
          ring_seconds: callMetaRef.current.startedAt
            ? Math.max(
                0,
                Math.round((Date.now() - callMetaRef.current.startedAt) / 1000)
              )
            : null,
        });
      });

      const handleRemoteHangup = (endReason, call) => {
        const callSid = getCallSid(call);
        const connectedAt = callMetaRef.current.connectedAt;
        const userHangup = userHangupRef.current;
        const connectedMs = connectedAt ? Date.now() - connectedAt : null;
        const disconnectMeta = {
          call_id: callMetaRef.current.callId,
          call_sid: callSid,
          end_reason: endReason,
          user_hangup: userHangup,
          was_connected: Boolean(callMetaRef.current.connectedCaptured),
          connected_ms: connectedMs,
          status: typeof connection.status === "function" ? connection.status() : null,
        };

        capturePostHogLog(
          userHangup ? "info" : "warn",
          "twilio_call_disconnected",
          disconnectMeta
        );

        const shouldError = shouldSurfaceUnexpectedDisconnect({
          userHangup,
          alreadyErrored: failureSurfaced,
          connectedAt,
        });

        if (shouldError) {
          // With answerOnBridge, busy / no-answer also end before "accept", so this
          // stays a warning until the dial lookup says it was a real failure.
          capturePostHogLog(
            connectedAt ? "error" : "warn",
            "twilio_call_unexpected_disconnect",
            disconnectMeta
          );

          const parentSid =
            typeof callSid === "string" && /^CA[0-9a-fA-F]{32}$/.test(callSid)
              ? callSid
              : null;

          if (!parentSid) {
            console.error(
              "Twilio browser call disconnected before reaching the destination",
              disconnectMeta
            );
            handleCallEnded("failed");
            surfaceCallFailure(getUnexpectedDisconnectMessage());
            return;
          }

          void (async () => {
            let lookup;
            try {
              lookup = await fetchDialResultWithRetry({
                organizationId: orgId,
                callSid: parentSid,
              });
            } catch (lookupError) {
              if (browserCallAttemptRef.current !== attemptId || failureSurfaced) {
                return;
              }
              console.error(
                "Twilio lookup-dial-result failed",
                lookupError?.response?.data || lookupError?.message || lookupError
              );
              capturePostHogLog("error", "twilio_lookup_dial_result_failed", {
                ...disconnectMeta,
                status: lookupError?.response?.status,
                error:
                  lookupError?.response?.data?.error ||
                  lookupError?.message ||
                  "lookup_failed",
              });
              handleCallEnded("failed");
              surfaceCallFailure(getUnexpectedDisconnectMessage());
              return;
            }

            if (browserCallAttemptRef.current !== attemptId || failureSurfaced) {
              return;
            }

            const dialResult = lookup?.dialResult ?? null;
            capturePostHogLog(
              dialResult?.errorCode ? "error" : "warn",
              "twilio_lookup_dial_result",
              {
                ...disconnectMeta,
                child_sid: dialResult?.sid ?? null,
                dial_status: dialResult?.status ?? null,
                error_code: dialResult?.errorCode ?? null,
                error_message: dialResult?.errorMessage ?? null,
              }
            );

            if (!shouldShowDialResultError(dialResult)) {
              const unanswered = getUnansweredOutcome(dialResult);
              if (unanswered) {
                setCallEndNotice(unanswered === "busy" ? "Busy" : "No Answer");
              }
              handleCallEnded(unanswered || "disconnect", { closeModal: false });
              return;
            }

            console.error(
              "Twilio browser call disconnected before reaching the destination",
              disconnectMeta
            );
            handleCallEnded("failed");
            surfaceCallFailure(getDialResultMessage(dialResult));
          })();
          return;
        }

        if (voicemailDropRef.current) {
          handleCallEnded("voicemail_dropped", { closeModal: false });
          return;
        }

        handleCallEnded(userHangup ? "user_hangup" : endReason);
      };

      connection.on("disconnect", (call) => handleRemoteHangup("disconnect", call));
      connection.on("cancel", (call) => handleRemoteHangup("cancel", call));
      connection.on("reject", (call) => handleRemoteHangup("reject", call));
      connection.on("error", (error) => {
        console.error("Twilio call error:", error);
        capturePostHogLog("error", "twilio_call_error", {
          code: error?.code,
          message: error?.message,
          call_id: callMetaRef.current.callId,
          call_sid: getCallSid(),
        });
        surfaceCallFailure(getTwilioErrorMessage(error));
        handleCallEnded("error");
      });
    } catch (error) {
      // Log for production diagnostics when placing browser call fails
      console.error("Dailer: browser call failed", error?.response?.data || error?.message || error);
      capturePostHogLog("error", "twilio_call_connect_failed", {
        message: error?.message,
        code: error?.code,
        status: error?.response?.status,
      });
      captureAnalyticsEvent("call_ended", {
        call_method: "browser_call",
        duration_seconds: 0,
        end_reason: "connect_failed",
        was_connected: false,
      });
      fireOutboundCompleteIfNeeded();
      setErrrorModal(true);
      const status = error?.response?.status;
      const apiError = error?.response?.data?.error;
      const apiMessage = error?.response?.data?.message;
      if (
        status === 401 &&
        typeof apiError === "string" &&
        apiError.toLowerCase().includes("authorization header")
      ) {
        setErrorMsg(
          "We ran into an authentication issue and can’t verify your session. Please log out and log back in."
        );
      } else if (error?.code != null) {
        setErrorMsg(getTwilioErrorMessage(error));
      } else if (error.response && error.response.data) {
        setErrorMsg([apiError, apiMessage].filter(Boolean).join(" "));
      } else {
        setErrorMsg("We couldn’t place the browser call. Please try again.");
      }
    } finally {
      setLoader(false);
    }
  }
  useEffect(() => {
    const recipientNumber =
      searchParams.get("recipient_number") || searchParams.get("recipient");
    if (!recipientNumber) return;

    const raw = decodeURIComponent(recipientNumber);
    setCallData((prev) => ({
      ...prev,
      recipient: formatPhoneNumber(raw) || raw,
    }));
    resolvePrefillContact(raw);

    const params = new URLSearchParams(searchParams);
    params.delete("recipient_number");
    params.delete("recipient");
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams, resolvePrefillContact]);

  const isBrowserCall = call_data?.callMethod === "browser_call";
  const recordingOn =
    outboundRecordingPolicy === "require" ||
    (outboundRecordingPolicy === "allow" && enableRecording);
  const activeCallOptions = [
    recordingOn && "Recording",
    (outboundTranscriptionPolicy === "require" ||
      (outboundTranscriptionPolicy === "allow" && enableTranscription && recordingOn)) &&
      "Transcription",
    shouldSyncOutboundNotification({
      channelId: outboundNotificationChannelId,
      policy: outboundNotificationPolicy,
      userEnabled: enableNotification,
    }) && "Sync notification",
    isBrowserCall &&
      greetingRecording &&
      (dialerPrefs.auto_greeting ? "Auto greeting" : "Greeting"),
    isBrowserCall && vmDropRecording && "Voicemail drop",
  ].filter(Boolean);

  return (
    <div className="pt-7 px-4">
      <div className="lg:text-[22px] text-[20px] font-bold mb-4 lg:mt-0 mt-8 ">
        Dialer
      </div>

      <OngoingCallModal
        open={openOngoingCallModal}
        setOpen={setOpenOngoingCallModal}
        call_data={call_data}
        device={device}
        connection={conn}
        onUserHangup={(isUserHangup = true) => {
          userHangupRef.current = isUserHangup;
        }}
        onVoicemailDropChange={(active) => {
          voicemailDropRef.current = active;
        }}
        endNotice={callEndNotice}
        greetingMixerRef={greetingMixerRef}
        greetingRecording={greetingRecording}
        vmDropRecording={vmDropRecording}
        autoGreeting={dialerPrefs.auto_greeting}
        activeCallInfo={activeCallInfo}
      />
      <SuccessModal
        open={succeesModal}
        setOpen={setSuccessModal}
        call_data={call_data}
        setCallData={setCallData}
        decodeToken={decodeToken}
      />

      <ErrorModal
        open={errorModal}
        setOpen={setErrrorModal}
        call_data={call_data}
        setCallData={setCallData}
        err_msg={err_msg}
        setErrorMsg={setErrorMsg}
        decodeToken={decodeToken}
      />

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={twilioCredsSnackbarOpen}
        autoHideDuration={8000}
        onClose={() => setTwilioCredsSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setTwilioCredsSnackbarOpen(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          There’s a problem with your Twilio API credentials.{" "}
          <Link
            to="/dashboard/settings/twilio-integration"
            style={{ fontWeight: 600, textDecoration: "underline" }}
            onClick={() => setTwilioCredsSnackbarOpen(false)}
          >
            Update in Settings
          </Link>
        </Alert>
      </Snackbar>

      <div className="flex justify-center">
        <div className="bg-white rounded-[13px]  lg:w-[640px] w-full  lg:px-14 px-5 py-5 flex justify-center border-[1px] border-gray-200">
          <div className="space-y-5 bg-white w-full ">
            <div className="lg:flex w-full items-center lg:space-x-10 ">
              <div className="w-full">
                <div className="lg:text-[14px] text-[14px] font-semibold flex items-center mb-2">
                  Recipient
                </div>
                <RecipientContactInput
                  value={call_data.recipient}
                  onChange={(next) => {
                    setRecipientError(null);
                    setCallData((prev) => ({
                      ...prev,
                      recipient: next,
                    }));
                  }}
                  error={recipientError}
                  selectedContact={selectedContact}
                  onSelectedContactChange={setSelectedContact}
                  organizationId={
                    organization?.orgId?.S ||
                    JSON.parse(localStorage.getItem("organization"))?.orgId?.S
                  }
                />
              </div>
            </div>
            <div className="lg:flex w-full items-center lg:space-x-10">
              <div className="w-full">
                <div className="lg:text-[14px] text-[14px] font-semibold flex items-center mb-2">
                  Caller ID
                </div>

                <Autocomplete
                  value={

                    from_numbers?.find(
                      (n) => n.phoneNumber === call_data.fromNumber
                    ) || null
                  }
                  onChange={(e, newValue) => {
                    setCallData((prev) => ({
                      ...prev,
                      fromNumber: newValue ? newValue.phoneNumber : "",
                    }));
                  }}
                  onOpen={() => {
                    // Fetch phone numbers when dropdown opens (only once)
                    if (!phoneNumbersLoaded && !loadingPhoneNumbers) {
                      getIncomingPhoneNumbers();
                    }
                  }}
                  options={from_numbers || []}
                  loading={loadingPhoneNumbers}
                  getOptionLabel={(option) =>
                    getPhoneOptionLabel(option)
                  }
                  renderInput={(params) => (
                    <InputBase
                      {...params.InputProps}
                      inputProps={params.inputProps}
                      placeholder="Select Caller ID"
                      sx={{
                        bgcolor: "white",
                        width: "100%",
                        px: 2,
                        py: 1.2,
                        fontSize: "16px",
                        border: "1px solid #D1D5DB",
                        borderRadius: "8px",
                      }}
                      endAdornment={
                        <>
                          {loadingPhoneNumbers ? (
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                          ) : (
                            params.InputProps.endAdornment
                          )}
                        </>
                      }
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      {getPhoneOptionLabel(option)}
                    </li>
                  )}
                  popupIcon={<KeyboardArrowDown />}
                  disableClearable
                  fullWidth
                />
              </div>
            </div>

            <div className="lg:flex w-full items-center lg:space-x-10  ">
              <div className="w-full">
                <div className="lg:text-[14px] text-[14px] font-semibold flex items-center mb-2">
                  Call Method
                </div>
                <div className="lg:flex items-center  lg:space-x-3 lg:space-y-0 space-y-3 ">
                  <div
                    onClick={() => {
                      if (
                        !JSON.parse(localStorage.getItem("organization"))
                          .twilio_integration?.M?.app_sid?.S
                      ) {
                        document.getElementById(
                          "nobrowsercallfunctionality"
                        ).style.display = "block";
                      } else {
                        setCallData((prev) => ({
                          ...prev,
                          callMethod: "browser_call",
                        }));
                      }
                    }}
                    className={
                      call_data.callMethod == "browser_call"
                        ? "cursor-pointer text-brand-primary w-full border-brand-primary bg-brand-mist border-[1.8px] px-4 py-2 rounded-md flex items-center space-x-3"
                        : "cursor-pointer w-full border-gray-100 border-[1.8px] px-4 py-2 rounded-md flex items-center space-x-3"
                    }
                  >
                    <div>
                      <CampaignOutlined
                        sx={
                          call_data.callMethod == "browser_call"
                            ? { color: brand.primary }
                            : { color: "#e5e5e5" }
                        }
                      />
                    </div>

                    <div>
                      <div className="lg:text-[16px] text-[14px] font-bold ">
                        Browser Call
                      </div>
                      <div className="lg:text-[14px] text-[12px]  text-gray-400">
                        Call from browser
                      </div>
                      <div
                        className="text-[13px] lg:text-[13px] text-yellow-600 "
                        hidden={
                          !JSON.parse(localStorage.getItem("organization"))
                            ?.twilio_integration?.M?.app_sid?.S
                            ? false
                            : true
                        }
                        id="nobrowsercallfunctionality"
                      >
                        <span>
                          <Info sx={{ fontSize: "" }} />
                        </span>{" "}
                        You don’t have browser calling configured
                      </div>
                    </div>
                  </div>

                  <div
                    onClick={() => {
                      setCallData((prev) => ({
                        ...prev,
                        callMethod: "forwarding_device",
                      }));
                    }}
                    className={
                      call_data.callMethod == "forwarding_device"
                        ? "cursor-pointer text-brand-primary w-full border-brand-primary bg-brand-mist border-[1.8px] px-4 py-2 rounded-md flex items-center space-x-3"
                        : "cursor-pointer w-full border-gray-100  border-[1.8px] px-4 py-2 rounded-md flex items-center space-x-3"
                    }
                  >
                    <div>
                      <CallOutlined
                        sx={
                          call_data.callMethod == "forwarding_device"
                            ? { color: brand.primary }
                            : { color: "#e5e5e5" }
                        }
                      />
                    </div>

                    <div>
                      <div className="lg:text-[16px] text-[14px] font-bold ">
                        Forwarding Device
                      </div>
                      <div className="lg:text-[14px] text-[12px]  text-gray-400">
                        Call your device first
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {call_data?.callMethod == "forwarding_device" && (
              <div className="lg:flex justify-end items-center lg:space-x-10 ">
                <div className="w-full">
                  <div className="lg:text-[14px] text-[14px] font-semibold flex items-center mb-2">
                    Forwarding Number
                  </div>
                  <div className="text-[12px] text-gray-500 mb-2">
                    Twilanswer will call this number first, then connect you to your recipient
                  </div>
                  <Select
                    IconComponent={KeyboardArrowDown}
                    fullWidth
                    defaultValue="Forward w/ VM"
                    value={call_data.forwarding_number}
                    onChange={(e) => {
                      setCallData((prev) => ({
                        ...prev,
                        forwarding_number: e.target.value,
                      }));
                    }}
                    className="mb-2 bg-white"
                    sx={{
                      fontSize: "16px",
                      padding: "16px 1px", // Adjust padding here
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                      "& .MuiSelect-select": {
                        padding: "16px ", // Adjust padding for the text inside Select
                        border: "1px solid #D1D5DB",
                        borderRadius: "8px",
                        backgroundColor: "white",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                    }}
                  >
                    {JSON.parse(localStorage.getItem("organization"))
                      .channels?.L.filter(
                        (e) => e.M.type.S === "forward_number"
                      )
                      .map((item, index) => {
                        return (
                          <MenuItem value={item.M.value.S}>
                            {formatPhoneNumber(item.M.value.S)} -{" "}
                            {item.M.label.S}
                          </MenuItem>
                        );
                      })}

                    <MenuItem
                      sx={{
                        textAlign: "center",
                        textTransform: "none",
                        bgcolor: "white",
                        color: brand.primary,
                        px: "2px",
                        borderRadius: "7px",
                        fontSize: { lg: "14px", xs: "13px" },
                        width: "100%",
                      }}
                      onClick={() => {
                        handleNavigation(
                          "/dashboard/channels?channeltype=forward_number"
                        );
                      }}
                    >
                      <div className="text-center w-full">
                        {" "}
                        Add Phone Number
                      </div>
                    </MenuItem>
                  </Select>
                </div>
              </div>
            )}

            {onTwilioTrial && <TwilioTrialBanner className="mb-2" />}

            {call_data?.callMethod == "browser_call" && 
              !JSON.parse(localStorage.getItem("organization"))
                ?.twilio_integration?.M?.app_sid?.S && (
              <div className="border-[1px] border-[#FFEF8A] text-[#854D0F] bg-[#FEFCE8] px-6 py-2 ">
                <div className="flex items-center text-[14px] font-bold text-[#854D0F] ">
                  {" "}
                  <TriangleAlert className="text-[#FACB14] w-[16px] mr-2" />{" "}
                  Browser Calling Not Configured{" "}
                </div>
                <div className="text-[#854D0F] text-[14px] my-2 ml-6">
                  {" "}
                  Browser calling requires additional setup in your organization
                  settings. Please configure your API credentials and TwiML App
                  to enable browser calling.
                </div>
                <div 
                  className="text-[13px] bg-[#FFEF8A] text-[#854D0F] w-fit px-1 rounded-[2px] ml-6 cursor-pointer hover:bg-[#d5c13f]"
                  onClick={() => navigate("/dashboard/settings/organization")}
                >
                  {" "}
                  Go To Settings <ArrowForward />{" "}
                </div>
              </div>
            )}

            {call_data?.callMethod === "browser_call" &&
              !!JSON.parse(localStorage.getItem("organization"))
                ?.twilio_integration?.M?.app_sid?.S && (
              <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
                <Tooltip
                  title={
                    deviceStatus === "connecting"
                      ? "Connecting to Twilio…"
                      : "Click for Twilio connection options"
                  }
                  arrow
                  placement="top"
                >
                  <button
                    type="button"
                    disabled={deviceStatus === "connecting"}
                    onClick={(e) => {
                      if (deviceStatus === "connecting") return;
                      setDeviceMenuAnchor(e.currentTarget);
                    }}
                    className={
                      deviceStatus === "registered"
                        ? "bg-[rgba(0,186,37,0.1)] text-[#166534] text-[12px] lg:text-[13px] px-3 py-1 text-center font-semibold rounded-[15px] inline-flex items-center gap-1.5 cursor-pointer hover:brightness-95"
                        : deviceStatus === "connecting"
                        ? "bg-[rgba(245,158,11,0.12)] text-[#92400E] text-[12px] lg:text-[13px] px-3 py-1 text-center font-semibold rounded-[15px] inline-flex items-center gap-1.5 cursor-default"
                        : deviceStatus === "error" ||
                          deviceStatus === "unregistered"
                        ? "bg-[#FEE2E2] text-[#991B1B] text-[12px] lg:text-[13px] px-3 py-1 text-center font-semibold rounded-[15px] inline-flex items-center gap-1.5 cursor-pointer hover:brightness-95"
                        : "bg-gray-100 text-gray-600 text-[12px] lg:text-[13px] px-3 py-1 text-center font-semibold rounded-[15px] inline-flex items-center gap-1.5 cursor-pointer hover:brightness-95"
                    }
                  >
                    {deviceStatus === "connecting" ? (
                      <CircularProgress
                        size="0.75rem"
                        sx={{ color: "#92400E", animationDuration: "0.7s" }}
                      />
                    ) : (
                      <span
                        className={
                          deviceStatus === "registered"
                            ? "w-2 h-2 rounded-full bg-[#34C759] inline-block"
                            : deviceStatus === "error" ||
                              deviceStatus === "unregistered"
                            ? "w-2 h-2 rounded-full bg-[#FF3B30] inline-block"
                            : "w-2 h-2 rounded-full bg-gray-400 inline-block"
                        }
                      />
                    )}
                    {deviceStatus === "registered"
                      ? "Twilio Connected"
                      : deviceStatus === "connecting"
                      ? "Connecting…"
                      : deviceStatus === "error"
                      ? "Twilio Connection Failed"
                      : deviceStatus === "unregistered"
                      ? "Twilio Disconnected"
                      : "Twilio Not Connected"}
                    {deviceStatus !== "connecting" && (
                      <KeyboardArrowDown sx={{ fontSize: 16, ml: -0.25 }} />
                    )}
                  </button>
                </Tooltip>
                <Menu
                  anchorEl={deviceMenuAnchor}
                  open={Boolean(deviceMenuAnchor)}
                  onClose={() => setDeviceMenuAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                  transformOrigin={{ vertical: "top", horizontal: "center" }}
                >
                  {deviceStatus === "registered" ? (
                    <>
                      <MenuItem
                        onClick={() => {
                          setDeviceMenuAnchor(null);
                          captureAnalyticsEvent("device_reconnect_clicked", {
                            action: "reconnect",
                            device_state: deviceStatus,
                          });
                          setUpDevice({ force: true, source: "user_reconnect" });
                        }}
                      >
                        Reconnect
                      </MenuItem>
                      <MenuItem onClick={disconnectDevice}>Disconnect</MenuItem>
                    </>
                  ) : (
                    <MenuItem
                      onClick={() => {
                        setDeviceMenuAnchor(null);
                        captureAnalyticsEvent("device_reconnect_clicked", {
                          action: "connect",
                          device_state: deviceStatus,
                        });
                        setUpDevice({ force: true, source: "user_connect" });
                      }}
                    >
                      Connect
                    </MenuItem>
                  )}
                </Menu>
              </div>
            )}

            <div className="pt-4">
              <button
                type="button"
                onClick={() => setAdvancedOpen((prev) => !prev)}
                className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                {advancedOpen ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
                <span>Advanced Settings</span>
              </button>
              {!advancedOpen && activeCallOptions.length > 0 && (
                <div className="mt-1 ml-5 text-[12px] text-gray-500">
                  {activeCallOptions.join(" · ")}
                </div>
              )}
              {advancedOpen && (
                <div className="mt-3">
                  {/* Recording Toggle */}
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-[14px] text-gray-700">
                      Record Call
                    </span>
                    {outboundRecordingPolicy === "disallow" ? (
                      <Tooltip
                        title="Call recording is disabled by your organization's policy. Contact your administrator to enable recording."
                        arrow
                      >
                        <span>
                          <Switch
                            checked={false}
                            disabled={true}
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-disabled": {
                                color: "#D1D5DB",
                              },
                              "& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track": {
                                backgroundColor: "#E5E7EB",
                              },
                            }}
                          />
                        </span>
                      </Tooltip>
                    ) : outboundRecordingPolicy === "require" ? (
                      <Tooltip
                        title="Call recording is required by your organization's policy and cannot be disabled."
                        arrow
                      >
                        <span>
                          <Switch
                            checked={true}
                            disabled={true}
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": {
                                color: brand.primary,
                              },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                backgroundColor: brand.primary,
                              },
                            }}
                          />
                        </span>
                      </Tooltip>
                    ) : (
                      <Switch
                        checked={enableRecording}
                        onChange={(e) => {
                          setEnableRecording(e.target.checked);
                          if (!e.target.checked) {
                            setEnableTranscription(false);
                          }
                        }}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: brand.primary,
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: brand.primary,
                          },
                        }}
                      />
                    )}
                  </div>

                  {/* Transcription Toggle */}
                  <div className="flex items-center justify-between">
                    <span className={`text-[14px] ${
                      outboundTranscriptionPolicy === "disallow" || outboundRecordingPolicy === "disallow"
                        ? "text-gray-400"
                        : "text-gray-700"
                    }`}>
                      Transcribe Call
                    </span>
                    {outboundTranscriptionPolicy === "disallow" || outboundRecordingPolicy === "disallow" ? (
                      <Tooltip
                        title={
                          outboundRecordingPolicy === "disallow"
                            ? "Transcription requires call recording to be enabled. Recording is currently disabled by your organization's policy."
                            : "Call transcription is disabled by your organization's policy. Contact your administrator to enable transcription."
                        }
                        arrow
                      >
                        <span>
                          <Switch
                            checked={false}
                            disabled={true}
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-disabled": {
                                color: "#D1D5DB",
                              },
                              "& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track": {
                                backgroundColor: "#E5E7EB",
                              },
                            }}
                          />
                        </span>
                      </Tooltip>
                    ) : outboundTranscriptionPolicy === "require" ? (
                      <Tooltip
                        title="Call transcription is required by your organization's policy and cannot be disabled."
                        arrow
                      >
                        <span>
                          <Switch
                            checked={true}
                            disabled={true}
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": {
                                color: brand.primary,
                              },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                backgroundColor: brand.primary,
                              },
                            }}
                          />
                        </span>
                      </Tooltip>
                    ) : (
                      <Switch
                        checked={enableTranscription}
                        onChange={(e) => setEnableTranscription(e.target.checked)}
                        disabled={!enableRecording}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: brand.primary,
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: brand.primary,
                          },
                          "& .MuiSwitch-switchBase.Mui-disabled": {
                            color: "#D1D5DB",
                          },
                          "& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track": {
                            backgroundColor: "#E5E7EB",
                          },
                        }}
                      />
                    )}
                  </div>

                  {/* Sync Notification Toggle */}
                  <div className="mt-3 flex items-center justify-between">
                    <span
                      className={`text-[14px] ${
                        !hasNotificationChannel
                          ? "text-gray-400"
                          : "text-gray-700"
                      }`}
                    >
                      Sync Notification
                    </span>
                    {!hasNotificationChannel ? (
                      <Tooltip
                        title="No outbound notification channel is configured. Ask your administrator to set one in Organization settings."
                        arrow
                      >
                        <span>
                          <Switch
                            checked={false}
                            disabled={true}
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-disabled": {
                                color: "#D1D5DB",
                              },
                              "& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track": {
                                backgroundColor: "#E5E7EB",
                              },
                            }}
                          />
                        </span>
                      </Tooltip>
                    ) : outboundNotificationPolicy === "require" ? (
                      <Tooltip
                        title="Outbound call notifications are required by your organization's policy."
                        arrow
                      >
                        <span>
                          <Switch
                            checked={true}
                            disabled={true}
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": {
                                color: brand.primary,
                              },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                backgroundColor: brand.primary,
                              },
                            }}
                          />
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip
                        title="Send call details to the configured notification channel."
                        arrow
                      >
                        <span>
                          <Switch
                            checked={enableNotification}
                            onChange={(e) =>
                              setEnableNotification(e.target.checked)
                            }
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": {
                                color: brand.primary,
                              },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                backgroundColor: brand.primary,
                              },
                            }}
                          />
                        </span>
                      </Tooltip>
                    )}
                  </div>

                  {isBrowserCall && (
                    <DialerAudioOptions
                      recordings={recordings}
                      setRecordings={setRecordings}
                      preferences={dialerPrefs}
                      onChange={handleDialerPrefsChange}
                      saveError={prefsSaveError}
                    />
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-center items-center gap-3 pt-6 pb-4">
              <Button
                sx={{
                  textTransform: "none",
                  bgcolor: "green",
                  color: "white",
                  py: "10px",
                  px: "20px",
                  borderRadius: "7px",
                  fontSize: { lg: "17px", xs: "14px" },
                  opacity:
                    call_data.callMethod === "browser_call" &&
                    deviceStatus === "connecting"
                      ? 0.7
                      : 1,
                }}
                disabled={
                  call_data.callMethod === "browser_call" &&
                  deviceStatus === "connecting"
                }
                onClick={() => {
                  if (loader) return;
                  const toNumber = toDialableNumber(call_data?.recipient);
                  if (toNumber.replace(/\D/g, "").length < 2) {
                    setRecipientError(
                      "Enter a phone number or pick a contact from the list."
                    );
                    return;
                  }
                  if (toNumber !== call_data.recipient) {
                    setCallData((prev) => ({ ...prev, recipient: toNumber }));
                  }
                  if (call_data.callMethod == "forwarding_device") {
                    call(toNumber);
                  } else {
                    const mixer = greetingMixerRef.current;
                    if (mixer) {
                      mixer.resume();
                      if (greetingRecording) mixer.preload(greetingRecording.url);
                    }
                    makeCall(toNumber);
                  }
                }}
              >
                {loader ? (
                  <CircularProgress
                    size={"1.2rem"}
                    sx={{ color: "white", animationDuration: "0.7s" }}
                  />
                ) : (
                  <>
                    <PhoneOutgoing className="w-[20px] mr-2  " />
                    Start Call
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dailer;
